// ** react
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

// ** react form hooks imports
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// ** custom hooks
import { useTaxFileDetails } from './TaxFileDetailsContext';

// ** api custom hooks
import { useAddNotes } from '../hooks/apis/useTaxFile';
import { useUpdateSectionAnswerByAdmin } from '../hooks/apis/useQuizResponse';

// ** enums imports
import { INPUT_TYPES } from '../enums/common';

const OPERATOR_MAP = {
  equals: 'eq',
  not_equals: 'ne',
  in: 'in',
  not_in: 'nin',
  contains: 'contains',
  includes: 'contains',
  has: 'contains',
  not_contains: 'ncontains',
  not_includes: 'ncontains',
  does_not_contain: 'ncontains',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
};

const normalizeOperator = op => {
  const normalized = (op || 'eq').toLowerCase();
  return OPERATOR_MAP[normalized] || normalized;
};

const normalizeInputType = type => {
  const t = (type || '').toLowerCase();
  if (t === INPUT_TYPES.DROPDOWN) return 'select';
  if (t === INPUT_TYPES.CHECKBOX_MULTIPLE) return 'multi-select';
  if (t === 'dob' || t === 'date_of_birth') return 'date';
  return t;
};

const normalizeValue = value => {
  if (value == null) return value;
  if (typeof value === 'object') {
    if (Array.isArray(value)) return value.map(normalizeValue);
    if ('value' in value) return value.value;
    if ('label' in value) return value.label;
    return JSON.stringify(value);
  }
  return value;
};

const parseDate = date => {
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof date === 'string') {
    if (date.includes('T')) return date.split('T')[0];
    const match = date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, mm, dd, yyyy] = match;
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  return date;
};

const normalizePersistedAnswer = (type, answer) => {
  if (answer == null) return answer;

  const normalizedType = normalizeInputType(type);

  // Handle select/dropdown/radio types
  if (
    ['select', INPUT_TYPES.RADIO, INPUT_TYPES.DROPDOWN].includes(normalizedType)
  ) {
    return typeof answer === 'object' && answer && 'value' in answer
      ? answer.value
      : answer;
  }

  // Handle multi-select/checkbox types
  if (
    [
      'multi-select',
      INPUT_TYPES.CHECKBOX,
      INPUT_TYPES.CHECKBOX_MULTIPLE,
    ].includes(normalizedType)
  ) {
    if (Array.isArray(answer)) {
      return answer.map(v =>
        v && typeof v === 'object' && 'value' in v ? v.value : v
      );
    }
    return [
      typeof answer === 'object' && answer && 'value' in answer
        ? answer.value
        : answer,
    ].filter(v => v != null);
  }

  // Handle boolean/toggle
  if (['boolean', 'toggle'].includes(normalizedType)) {
    return typeof answer === 'string'
      ? answer.toLowerCase() === 'true'
      : Boolean(answer);
  }

  // Handle dates
  if ([INPUT_TYPES.DATE, 'date'].includes(normalizedType)) {
    return parseDate(answer);
  }

  // Handle dynamic groups
  if (normalizedType === INPUT_TYPES.DYNAMIC_GROUP) {
    if (Array.isArray(answer)) {
      // Check if already in expected format
      if (
        answer.length > 0 &&
        answer[0].questionId &&
        Array.isArray(answer[0].answer)
      ) {
        return answer.map(item => {
          const itemObj = {};
          if (Array.isArray(item.answer)) {
            item.answer.forEach(subItem => {
              itemObj[subItem.questionId] = subItem.answer;
            });
          }
          return itemObj;
        });
      }
      return answer;
    }
    // Try parsing string
    if (typeof answer === 'string') {
      try {
        const parsed = JSON.parse(answer);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Ignore parse errors
      }
    }
    // Convert single object to array
    if (answer && typeof answer === 'object') return [answer];
    return [];
  }

  return answer;
};

const evaluateRule = (value, operator, expected) => {
  const v = normalizeValue(value);
  const e = normalizeValue(expected);
  const op = normalizeOperator(operator);

  switch (op) {
    case 'eq':
      return Array.isArray(e) ? e.includes(v) : v === e;
    case 'ne':
      return Array.isArray(e) ? !e.includes(v) : v !== e;
    case 'in':
      return Array.isArray(e) ? e.includes(v) : false;
    case 'nin':
      return Array.isArray(e) ? !e.includes(v) : true;
    case 'contains':
      if (Array.isArray(v)) return v.includes(e);
      if (typeof v === 'string') return v.includes(String(e));
      return false;
    case 'ncontains':
      if (Array.isArray(v)) return !v.includes(e);
      if (typeof v === 'string') return !v.includes(String(e));
      return true;
    case 'gt':
      return Number(v) > Number(e);
    case 'gte':
      return Number(v) >= Number(e);
    case 'lt':
      return Number(v) < Number(e);
    case 'lte':
      return Number(v) <= Number(e);
    default:
      return true;
  }
};

const buildQuestionSignature = question => {
  const nestedFields = Array.isArray(question.where?.fields)
    ? question.where.fields
    : Array.isArray(question.fields)
      ? question.fields
      : undefined;

  return JSON.stringify({
    id: question.questionId,
    section: question.sectionId || question?.metadata?.sectionId,
    text: question.questionText,
    subtitle: question.questionSubtitle,
    type: question.inputType,
    placeholder: question.placeholder,
    hidden: question.isHidden,
    secure: question.isSecure,
    required: question.required,
    hasOptOut: question.hasOptOut,
    optedOut: question.optedOut,
    answer: question.answer,
    validation: question.validation,
    options: question.options,
    formats: question.acceptedFormats,
    maxFiles: question.maxFiles,
    maxFileSize: question.maxFileSize,
    nested: nestedFields?.map(f => ({
      id: f.questionId,
      t: f.questionType || f.inputType,
      r: !!f.required,
    })),
  });
};

const createFieldSchema = field => {
  const type = normalizeInputType(
    field.inputType || field.questionType || 'text'
  );
  let schema;

  switch (type) {
    case INPUT_TYPES.NUMBER:
    case INPUT_TYPES.CURRENCY:
      schema = yup.number().typeError('Must be a number');
      if (field.validation?.min != null) {
        schema = schema.min(
          field.validation.min,
          `Minimum value is ${field.validation.min}`
        );
      }
      if (field.validation?.max != null) {
        schema = schema.max(
          field.validation.max,
          `Maximum value is ${field.validation.max}`
        );
      }
      break;

    case INPUT_TYPES.EMAIL:
      schema = yup.string().email('Invalid email address');
      break;

    case INPUT_TYPES.DATE:
    case 'date':
      schema = yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date');
      break;

    case INPUT_TYPES.CHECKBOX:
      schema = yup.boolean();
      break;

    case 'multi-select':
    case INPUT_TYPES.CHECKBOX_MULTIPLE:
      schema = yup.array().of(yup.mixed());
      break;

    case INPUT_TYPES.SSN:
      // SSN is a string type but we'll apply specific validation rules later
      schema = yup.string();
      // For SSN, if no specific validation is provided, we can enforce 9 digits
      if (
        !field.validation?.pattern &&
        field.validation?.minLength === undefined &&
        field.validation?.maxLength === undefined
      ) {
        // Add default validation for 9 digits if no specific validation is provided
        schema = schema.matches(/^\d{9}$/, 'SSN must be 9 digits');
      }
      break;

    default:
      schema = yup.string();
  }

  if (field.required && typeof schema.required === 'function') {
    schema = schema.required('This field is required');
  }

  return schema;
};

const createGroupSchema = question => {
  const fields = Array.isArray(question.fields)
    ? question.fields
    : Array.isArray(question.where?.fields)
      ? question.where.fields
      : [];

  const itemShape = {};
  fields.forEach(field => {
    itemShape[field.questionId] = createFieldSchema(field);
  });

  let objSchema = yup.object().shape(itemShape);

  if (question.required) {
    objSchema = objSchema.test(
      'group-required',
      'This field is required',
      obj => {
        if (!obj || typeof obj !== 'object') return false;
        const requiredIds = fields
          .filter(f => f.required)
          .map(f => f.questionId);
        const candidates =
          requiredIds.length > 0 ? requiredIds : fields.map(f => f.questionId);

        return candidates.some(k => {
          const v = obj[k];
          if (v == null) return false;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === 'string') return v.trim().length > 0;
          if (typeof v === 'boolean') return true;
          if (typeof v === 'number') return !Number.isNaN(v);
          if (typeof v === 'object') return Object.keys(v).length > 0;
          return false;
        });
      }
    );
  }

  return objSchema;
};

const createDynamicGroupSchema = question => {
  const fields = Array.isArray(question.fields) ? question.fields : [];
  const itemShape = {};

  fields.forEach(field => {
    itemShape[field.questionId] = createFieldSchema(field);
  });

  let arrSchema = yup.array().of(yup.object().shape(itemShape));

  if (question.required && typeof arrSchema.min === 'function') {
    arrSchema = arrSchema.min(1, 'At least one item is required');
  }

  return arrSchema;
};

const applyValidationRules = (schema, question, type) => {
  if (!question.validation) return schema;

  const { validation } = question;
  const isNumeric =
    type === INPUT_TYPES.NUMBER || type === INPUT_TYPES.CURRENCY;

  // Special handling for SSN type
  if (type === 'ssn') {
    // If no specific pattern is provided but we have length constraints,
    // we should apply them to the cleaned (digits-only) value
    if (validation.pattern || validation.regex) {
      // Use the provided pattern
      const patternSource = validation.pattern || validation.regex;
      if (patternSource && typeof schema.matches === 'function') {
        try {
          const regex = new RegExp(patternSource);
          schema = schema.matches(
            regex,
            validation.errorMessage || 'Invalid format'
          );
        } catch (e) {
          // Ignore invalid regex
        }
      }
    } else {
      // If no pattern provided but we have length constraints, validate digits length
      if (
        validation.minLength !== undefined &&
        typeof schema.test === 'function'
      ) {
        schema = schema.test(
          'minLength',
          validation.errorMessage ||
            `Minimum length is ${validation.minLength}`,
          value => {
            if (!value) return true; // Let required validation handle empty values
            const digitsOnly = value.replace(/\D/g, '');
            return digitsOnly.length >= validation.minLength;
          }
        );
      }
      if (
        validation.maxLength !== undefined &&
        typeof schema.test === 'function'
      ) {
        schema = schema.test(
          'maxLength',
          validation.errorMessage ||
            `Maximum length is ${validation.maxLength}`,
          value => {
            if (!value) return true; // Let required validation handle empty values
            const digitsOnly = value.replace(/\D/g, '');
            return digitsOnly.length <= validation.maxLength;
          }
        );
      }
    }
  } else {
    // Min/Max Length (for strings)
    if (
      !isNumeric &&
      validation.minLength !== undefined &&
      typeof schema.min === 'function'
    ) {
      schema = schema.min(
        validation.minLength,
        validation.errorMessage || `Minimum length is ${validation.minLength}`
      );
    }
    if (
      !isNumeric &&
      validation.maxLength !== undefined &&
      typeof schema.max === 'function'
    ) {
      schema = schema.max(
        validation.maxLength,
        validation.errorMessage || `Maximum length is ${validation.maxLength}`
      );
    }

    // Min/Max Value (for numbers)
    if (
      isNumeric &&
      validation.min !== undefined &&
      typeof schema.min === 'function'
    ) {
      schema = schema.min(
        validation.min,
        validation.errorMessage || `Minimum value is ${validation.min}`
      );
    }
    if (
      isNumeric &&
      validation.max !== undefined &&
      typeof schema.max === 'function'
    ) {
      schema = schema.max(
        validation.max,
        validation.errorMessage || `Maximum value is ${validation.max}`
      );
    }

    // Pattern validation
    const patternSource = validation.pattern || validation.regex;
    if (patternSource && typeof schema.matches === 'function') {
      try {
        const regex = new RegExp(patternSource);
        schema = schema.matches(
          regex,
          validation.errorMessage || 'Invalid format'
        );
      } catch (e) {
        // Ignore invalid regex
      }
    }
  }

  return schema;
};

const createValidationSchema = questions => {
  const schemaFields = {};

  (questions || []).forEach(question => {
    if (!question || question.isHidden) return;

    const type = normalizeInputType(
      question.inputType || question.questionType
    );
    const optionValues = Array.isArray(question.options)
      ? question.options.map(o => o?.value)
      : [];

    let fieldSchema;

    // Handle complex types
    if (type === INPUT_TYPES.DYNAMIC_GROUP) {
      fieldSchema = createDynamicGroupSchema(question);
    } else if (type === 'group') {
      fieldSchema = createGroupSchema(question);
    } else if (type === INPUT_TYPES.FILE_UPLOAD || type === 'file_upload') {
      fieldSchema = yup
        .mixed()
        .test('file-validation', 'Please upload valid files', value => {
          if (!value) return !question.required;
          if (value instanceof FileList) return value.length > 0;
          if (Array.isArray(value)) {
            return (
              value.length > 0 &&
              value.every(
                item => item?.documentId || item?.name || item instanceof File
              )
            );
          }
          return false;
        });
    } else {
      // Handle simple types
      switch (type) {
        case INPUT_TYPES.NUMBER:
        case INPUT_TYPES.CURRENCY:
          fieldSchema = yup.number().typeError('Must be a number');
          break;
        case INPUT_TYPES.EMAIL:
          fieldSchema = yup.string().email('Invalid email address');
          break;
        case INPUT_TYPES.DATE:
        case 'date':
          fieldSchema = yup
            .string()
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date');
          break;
        case INPUT_TYPES.CHECKBOX:
          fieldSchema = yup.boolean();
          break;
        case 'multi-select':
        case INPUT_TYPES.CHECKBOX_MULTIPLE:
          fieldSchema = yup.array().of(yup.mixed());
          if (optionValues.length > 0) {
            fieldSchema = fieldSchema.of(yup.mixed().oneOf(optionValues));
          }
          break;
        case 'select':
        case INPUT_TYPES.RADIO:
        case INPUT_TYPES.DROPDOWN:
          fieldSchema = yup.mixed();
          if (optionValues.length > 0) {
            fieldSchema = fieldSchema.oneOf(optionValues);
          }
          break;
        case 'ssn':
          // For SSN, we want to validate it as a string with specific format if needed
          fieldSchema = yup.string();
          break;
        default:
          fieldSchema = yup.string();
      }

      // Apply additional validation rules
      fieldSchema = applyValidationRules(fieldSchema, question, type);
    }

    // Handle required fields
    if (question.required) {
      if (type === INPUT_TYPES.CHECKBOX) {
        fieldSchema = fieldSchema.oneOf([true], 'This field is required');
      } else if (
        type === 'multi-select' ||
        type === INPUT_TYPES.CHECKBOX_MULTIPLE
      ) {
        if (typeof fieldSchema.min === 'function') {
          fieldSchema = fieldSchema.min(1, 'At least one option is required');
        }
      } else if (typeof fieldSchema.required === 'function') {
        fieldSchema = fieldSchema.required('This field is required');
      }
    }

    schemaFields[question.questionId] = fieldSchema;
  });

  return yup.object().shape(schemaFields);
};

const QuizContext = createContext(null);

export function QuizProvider({ children }) {
  // States
  const [activeSection, setActiveSection] = useState(0);
  const [activeSectionData, setActiveSectionData] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveReason, setSaveReason] = useState('');
  const [sectionData, setSectionData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [existingAnswer] = useState([]);
  const [isHistoryMode, setIsHistoryMode] = useState(false);

  // Refs for caching and optimization
  const lastResetKeyRef = useRef(null);
  const questionCacheRef = useRef(new Map());
  const prevQuestionIdsRef = useRef([]);
  const schemaRef = useRef(yup.object().shape({}));

  // Custom hooks
  const {
    quizResponse,
    allSectionWithQuestion,
    refetchGetQuizResponseByTaxFileId,
  } = useTaxFileDetails();

  // API hooks
  const {
    updateSectionAnswerByAdmin,
    isUpdateSectionAnswerByAdminLoading,
    isUpdateSectionAnswerByAdminError,
    updateSectionAnswerByAdminError,
    isUpdateSectionAnswerByAdminSuccess,
  } = useUpdateSectionAnswerByAdmin();

  const { addNotes, isAddNotesLoading } = useAddNotes();

  const evaluateConditions = useCallback((conditions, getAnswer) => {
    if (!conditions || (Array.isArray(conditions) && conditions.length === 0)) {
      return true;
    }

    const evaluateOne = condition => {
      if (!condition) return true;
      const questionId = condition.dependsOn || condition.questionId;
      const operator = condition.condition || condition.operator;
      const expected = condition.value;
      const answer = getAnswer(questionId);
      return evaluateRule(answer, operator, expected);
    };

    if (Array.isArray(conditions)) {
      return conditions.every(evaluateOne);
    }

    if (typeof conditions === 'object') {
      if (Array.isArray(conditions.conditions)) {
        return conditions.conditions.every(evaluateOne);
      }
      if (Array.isArray(conditions.rules)) {
        return conditions.rules.every(evaluateOne);
      }
      return evaluateOne(conditions);
    }

    return true;
  }, []);

  const persistedAnswersMap = useMemo(() => {
    const map = {};
    const response = quizResponse?.quizResponse || quizResponse;
    const sections = response?.sectionSubmissions || [];

    sections.forEach(section => {
      (section.answers || []).forEach(answer => {
        const type = normalizeInputType(
          answer.questionType || answer.inputType
        );
        const normalized = normalizePersistedAnswer(type, answer.answer);
        map[answer.questionId] = normalized;
      });
    });

    return map;
  }, [quizResponse]);

  const currentSectionValidationSchema = useMemo(() => {
    if (!activeSectionData?.questions) return yup.object().shape({});
    return createValidationSchema(activeSectionData.questions);
  }, [activeSectionData]);

  useEffect(() => {
    schemaRef.current = currentSectionValidationSchema;
  }, [currentSectionValidationSchema]);

  const resolver = useCallback((data, context, options) => {
    return yupResolver(schemaRef.current)(data, context, options);
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
    clearErrors,
    unregister,
  } = useForm({
    resolver,
    mode: 'onChange',
  });

  const liveOverrides = useWatch({ control }) || {};

  const quizSections = useMemo(() => {
    const sections = Array.isArray(allSectionWithQuestion)
      ? allSectionWithQuestion
      : allSectionWithQuestion?.data || [];

    const getAnswer = questionId =>
      liveOverrides?.[questionId] !== undefined
        ? liveOverrides[questionId]
        : persistedAnswersMap[questionId];

    // Build conditional logic maps
    const questionTargetConditions = new Map();
    const sectionTargetConditions = new Map();

    sections.forEach(section => {
      (section?.questions || []).forEach(question => {
        const triggers = question?.conditionalLogic?.triggers;
        if (!Array.isArray(triggers) || triggers.length === 0) return;

        triggers.forEach(trigger => {
          if (!trigger) return;

          const condition = {
            dependsOn: question.questionId,
            condition: trigger.condition || trigger.operator || 'equals',
            value: trigger.value,
          };

          if (trigger.action === 'showQuestion' && trigger.target) {
            const existing = questionTargetConditions.get(trigger.target) || [];
            questionTargetConditions.set(trigger.target, [
              ...existing,
              condition,
            ]);
          } else if (trigger.action === 'showSection' && trigger.target) {
            const existing = sectionTargetConditions.get(trigger.target) || [];
            sectionTargetConditions.set(trigger.target, [
              ...existing,
              condition,
            ]);
          }
        });
      });
    });

    const combineConditions = (base, extras) => {
      if (!extras || extras.length === 0) return base;
      if (!base) return extras;
      return Array.isArray(base) ? [...base, ...extras] : [base, ...extras];
    };

    // Process sections
    return sections.map((section, index) => {
      const sectionNumber = section.sectionNumber || index + 1;
      const sectionId = section.sectionId || sectionNumber;

      const augmentedConditions = combineConditions(
        section.conditionalDisplay,
        sectionTargetConditions.get(sectionId)
      );
      const isVisible = evaluateConditions(augmentedConditions, getAnswer);

      // Process questions
      const rawQuestions = section.questions || [];
      const normalizedQuestions = rawQuestions.map((q, qIndex) => {
        const inputType = normalizeInputType(q.questionType);
        const questionAugmentedConditions = combineConditions(
          q.conditionalDisplay,
          questionTargetConditions.get(q.questionId)
        );
        const isHidden = !evaluateConditions(
          questionAugmentedConditions,
          getAnswer
        );
        const initialAnswer = getAnswer(q.questionId);

        // Check if admin modified
        const adminModified = (() => {
          const resp = quizResponse?.quizResponse || quizResponse;
          const secs = resp?.sectionSubmissions || [];
          for (const sec of secs) {
            const found = (sec.answers || []).find(
              a => a.questionId === q.questionId
            );
            if (found) return Boolean(found.adminModified);
          }
          return false;
        })();

        const isRequired = Boolean(q.required);
        const optedOut = Boolean(q.optedOut);
        const unanswered = (() => {
          if (!isRequired || optedOut) return false;
          if (initialAnswer == null) return true;
          if (Array.isArray(initialAnswer)) return initialAnswer.length === 0;
          if (typeof initialAnswer === 'string')
            return initialAnswer.trim().length === 0;
          return false;
        })();

        const proposed = {
          id: q.questionId,
          templateId: q.templateId,
          sectionId: section.sectionId,
          questionNumber: q.questionNumber || qIndex + 1,
          questionId: q.questionId,
          questionText: q.questionText || '',
          questionSubtitle: q.questionSubtitle || '',
          questionType: q.questionType,
          inputType,
          placeholder: q.placeholder,
          isHidden,
          secure: Boolean(q.encrypt),
          required: isRequired,
          hasOptOut: Boolean(q.hasOptOut),
          optedOut,
          answer: initialAnswer,
          rawAnswer: initialAnswer,
          validation: {
            minLength: q.validation?.minLength,
            maxLength: q.validation?.maxLength,
            pattern: q.validation?.regex || q.validation?.pattern,
            min: q.validation?.min,
            max: q.validation?.max,
            errorMessage: q.validation?.errorMessage,
          },
          options: (q.options || []).map(opt => ({
            value: opt.value,
            label: opt.label,
            exclusive: Boolean(opt.exclusive || opt.mutuallyExclusive),
          })),
          acceptedFormats:
            q.uploadConfig?.allowedTypes || q.acceptedFormats || [],
          maxFiles: q.maxFiles,
          maxFileSize: q.uploadConfig?.maxFileSize || q.maxFileSize,
          documentType:
            q.documentType ||
            (inputType === 'file_upload' ? 'OTHER' : undefined),
          taxFileId: quizResponse?.taxFileId,
          conditionalTrigger:
            q.conditionalTrigger ||
            (questionAugmentedConditions &&
            (Array.isArray(questionAugmentedConditions)
              ? questionAugmentedConditions.length > 0
              : true)
              ? {
                  targetQuestionIds: (Array.isArray(questionAugmentedConditions)
                    ? questionAugmentedConditions
                    : [questionAugmentedConditions]
                  )
                    .map(c => c?.dependsOn)
                    .filter(Boolean),
                  targetQuestionId: (Array.isArray(questionAugmentedConditions)
                    ? questionAugmentedConditions
                    : [questionAugmentedConditions]
                  ).find(c => c?.dependsOn)?.dependsOn,
                  condition: {
                    operator:
                      (Array.isArray(questionAugmentedConditions)
                        ? questionAugmentedConditions[0]?.condition
                        : questionAugmentedConditions?.condition) || 'equals',
                    value: Array.isArray(questionAugmentedConditions)
                      ? questionAugmentedConditions[0]?.value
                      : questionAugmentedConditions?.value,
                  },
                }
              : null),
          isValid: true,
          validationErrors: [],
          unanswered,
          adminModified,
          where: q.where,
          fields: q.fields,
          metadata: {
            templateId: q.templateId,
            sectionId: section.sectionId,
            questionNumber: q.questionNumber || qIndex + 1,
            questionId: q.questionId,
            inputType,
            required: isRequired,
            secure: Boolean(q.encrypt),
            hidden: isHidden,
            documentType: q.documentType,
            taxFileId: quizResponse?.taxFileId,
          },
        };

        // Cache optimization
        const cacheKey = `${sectionId}::${proposed.questionId}`;
        const signature = buildQuestionSignature(proposed);
        const cached = questionCacheRef.current.get(cacheKey);

        if (cached && cached.sig === signature) {
          return cached.obj;
        }

        questionCacheRef.current.set(cacheKey, {
          obj: proposed,
          sig: signature,
        });
        return proposed;
      });

      const requiredCount = normalizedQuestions.filter(q => q.required).length;

      return {
        id: sectionId,
        title: section.title || `Section ${sectionNumber}`,
        description: section.description,
        status: 'not_started',
        progress: 0,
        questions: normalizedQuestions,
        isVisible,
        isVisiblePersisted: isVisible,
        isOngoing: false,
        isCompleted: false,
        metadata: {
          sectionId: section.sectionId,
          sectionNumber,
          requiredQuestions: requiredCount,
          totalQuestions: normalizedQuestions.length,
          answeredQuestions: 0,
          invalidQuestions: 0,
        },
      };
    });
  }, [
    allSectionWithQuestion,
    liveOverrides,
    persistedAnswersMap,
    evaluateConditions,
    quizResponse,
  ]);

  useEffect(() => {
    const section = quizSections?.[activeSection];
    if (!section) {
      setSectionData(null);
      setQuestions([]);
      setActiveSectionData(null);
      return;
    }

    const sectionNumber = section?.metadata?.sectionNumber || activeSection + 1;
    const questionsArr = section.questions || [];

    // Reset form with defaults
    const defaults = {};
    questionsArr.forEach(q => {
      defaults[q.questionId] = q.answer;
    });

    const resetKey = JSON.stringify(defaults);
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      reset(defaults);
    }

    setQuestions([...questionsArr]);

    const derivedSectionData = {
      section: {
        sectionId:
          section.id || section.metadata?.sectionId || section.sectionId,
        sectionNumber,
      },
      questions: [...questionsArr],
      existingAnswers: questionsArr.map(q => ({
        questionId: q.questionId,
        answer: q.answer,
      })),
      sessionId: quizResponse?.quizResponse?.sessionId,
    };

    setSectionData(derivedSectionData);
    setActiveSectionData(derivedSectionData);
  }, [activeSection, quizSections, quizResponse, reset]);

  useEffect(() => {
    if (!Array.isArray(quizSections)) return;

    if (quizSections.length === 0 && activeSection !== 0) {
      setActiveSection(0);
      return;
    }

    if (activeSection > quizSections.length - 1) {
      setActiveSection(Math.max(0, quizSections.length - 1));
    }
  }, [quizSections, activeSection]);

  useEffect(() => {
    if (!Array.isArray(quizSections) || quizSections.length === 0) return;

    const current = quizSections[activeSection];
    if (current && current.isVisible) return;

    const nextVisible = quizSections.findIndex(s => s.isVisible);
    if (nextVisible !== -1 && nextVisible !== activeSection) {
      setActiveSection(nextVisible);
    }
  }, [quizSections, activeSection]);

  useEffect(() => {
    clearErrors();
  }, [activeSection, clearErrors]);

  useEffect(() => {
    const currentIds = (sectionData?.questions || []).map(q => q.questionId);
    const prevIds = prevQuestionIdsRef.current || [];

    prevIds.forEach(id => {
      if (!currentIds.includes(id)) unregister(id);
    });

    prevQuestionIdsRef.current = currentIds;
  }, [sectionData, unregister]);

  const handleInputChange = useCallback(
    (questionId, value) => {
      setValue(questionId, value, { shouldValidate: true });
    },
    [setValue]
  );

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev);
    if (isEditMode) {
      setSaveReason('');
    } else if (sectionData?.questions) {
      const defaults = {};
      sectionData.questions.forEach(q => {
        const existing = (sectionData.existingAnswers || []).find(
          a => a.questionId === q.questionId
        );
        if (existing) defaults[q.questionId] = existing.answer;
      });
      reset(defaults);
    }
  }, [isEditMode, sectionData, reset]);

  const toggleHistoryMode = useCallback(() => {
    setIsHistoryMode(prev => !prev);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (sectionData?.questions) {
      const defaults = {};
      sectionData.questions.forEach(q => {
        const existing = (sectionData.existingAnswers || []).find(
          a => a.questionId === q.questionId
        );
        if (existing) defaults[q.questionId] = existing.answer;
      });
      reset(defaults);
    }
    setSaveReason('');
    setIsEditMode(false);
  }, [sectionData, reset]);

  const toggleQuestionExpansion = useCallback(questionId => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  const setActiveSectionById = useCallback(
    sectionId => {
      if (!sectionId) return;
      const idx = (quizSections || []).findIndex(
        s => s.id === sectionId || s?.metadata?.sectionId === sectionId
      );
      if (idx >= 0) setActiveSection(idx);
    },
    [quizSections]
  );

  const handleSaveQuiz = useCallback(
    async data => {
      if (!activeSectionData) throw new Error('No section data available');

      const visibleQuestions = (activeSectionData.questions || []).filter(
        q => !q.isHidden
      );

      const answers = visibleQuestions
        .map(q => {
          const raw = data[q.questionId];
          const isArrayType = [
            'multi-select',
            INPUT_TYPES.CHECKBOX_MULTIPLE,
            INPUT_TYPES.DYNAMIC_GROUP,
          ].includes(q.inputType);

          const answer = raw !== undefined ? raw : isArrayType ? [] : null;

          return {
            questionId: q.questionId,
            sectionId: sectionData.section?.sectionId,
            answer,
            inputType: q.inputType,
            required: !!q.required,
          };
        })
        .filter(({ answer, required }) => {
          if (required) return true;
          if (Array.isArray(answer)) return answer.length > 0;
          return answer !== null && answer !== '';
        })
        .map(({ questionId, sectionId, answer, inputType }) => {
          // Process DYNAMIC_GROUP answers
          let processedAnswer = answer;
          if (
            inputType === INPUT_TYPES.DYNAMIC_GROUP &&
            Array.isArray(answer)
          ) {
            processedAnswer = answer.map(item => ({
              questionId: questionId,
              answer: Object.entries(item).map(([fieldId, fieldValue]) => ({
                questionId: fieldId,
                answer: fieldValue,
              })),
            }));
          }

          return {
            questionId,
            sectionId,
            answer: processedAnswer,
          };
        });

      const sessionId =
        quizResponse?.quizResponse?.sessionId ||
        activeSectionData?.sessionId ||
        activeSectionData?.quizResponse?.id;

      if (!sessionId || typeof sessionId !== 'string') {
        console.error('Save aborted: missing/invalid session ID', sessionId);
        return;
      }

      const sectionNumber = activeSectionData.section?.sectionNumber;
      if (!Number.isInteger(sectionNumber) || sectionNumber < 1) {
        throw new Error('Valid section number required');
      }

      if (saveReason) {
        await addNotes(quizResponse.taxFile._id, { notes: saveReason });
      }
      await updateSectionAnswerByAdmin(sessionId, sectionNumber, {
        answers,
        reason: saveReason,
      });

      toggleEditMode();
      refetchGetQuizResponseByTaxFileId();
    },
    [
      questions,
      activeSectionData,
      sectionData,
      quizResponse,
      saveReason,
      addNotes,
      updateSectionAnswerByAdmin,
      toggleEditMode,
    ]
  );

  const handleEditQuiz = useCallback(() => {
    if (isEditMode) {
      handleSubmit(handleSaveQuiz)();
    } else {
      toggleEditMode();
    }
  }, [isEditMode, handleSubmit, handleSaveQuiz, toggleEditMode]);

  const sectionScopedErrorMessage = useMemo(() => {
    const questionIds = (activeSectionData?.questions || []).map(
      q => q.questionId
    );
    const hasError = questionIds.some(id => !!errors?.[id]);

    if (!hasError) return '';

    const sectionNum =
      activeSectionData?.section?.sectionNumber ?? activeSection + 1;
    return `Section ${sectionNum} is incomplete. Please fill out the required fields.`;
  }, [errors, activeSectionData, activeSection]);

  const value = useMemo(
    () => ({
      isHistoryMode,
      quizSections,
      visibleSections: (quizSections || []).filter(s => s.isVisible),
      activeSectionData,
      sectionData,
      questions,
      existingAnswer,
      activeSection,
      setActiveSection,
      setActiveSectionById,
      control,
      errors,
      handleInputChange,
      isEditMode,
      handleEditQuiz,
      handleCancelEdit,
      expandedQuestions,
      toggleQuestionExpansion,
      toggleHistoryMode,
      saveReason,
      setSaveReason,
      isUpdateSectionAnswerByAdminLoading,
      isUpdateSectionAnswerByAdminError,
      updateSectionAnswerByAdminError,
      isUpdateSectionAnswerByAdminSuccess,
      isCurrentSectionPersistedVisible:
        quizSections?.[activeSection]?.isVisiblePersisted !== false,
      allSectionWithQuestion,
      sectionScopedErrorMessage,
      addNotes,
      isAddNotesLoading,
    }),
    [
      isHistoryMode,
      quizSections,
      activeSectionData,
      sectionData,
      questions,
      existingAnswer,
      activeSection,
      setActiveSectionById,
      control,
      errors,
      handleInputChange,
      isEditMode,
      handleEditQuiz,
      handleCancelEdit,
      expandedQuestions,
      toggleQuestionExpansion,
      toggleHistoryMode,
      saveReason,
      isUpdateSectionAnswerByAdminLoading,
      isUpdateSectionAnswerByAdminError,
      updateSectionAnswerByAdminError,
      isUpdateSectionAnswerByAdminSuccess,
      allSectionWithQuestion,
      sectionScopedErrorMessage,
      addNotes,
      isAddNotesLoading,
    ]
  );

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
}

export function useQuiz() {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within QuizProvider');
  }
  return context;
}
